import { useEffect, useState } from "react";

import { fetchMe } from "@/api/me.api";
import { useAuth } from "@/auth/useAuth";
import { AuthError } from "@/types/auth";
import type { MeResponse } from "@/types/me";

export interface UseMeResult {
  readonly me: MeResponse | null;
  readonly loading: boolean;
  readonly error: Error | null;
  retry(): void;
}

export function useMe(): UseMeResult {
  const { firebaseUser, signOutUser } = useAuth();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!firebaseUser) {
      setMe(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchMe()
      .then((result) => {
        if (!cancelled) {
          setMe(result);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setLoading(false);
        setError(err);
        if (err instanceof AuthError) {
          void signOutUser();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [firebaseUser, attempt, signOutUser]);

  return {
    me,
    loading,
    error,
    retry: () => {
      setAttempt((a) => a + 1);
    },
  };
}
